import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Wrench, User, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

const AdeeflyProject = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const challenges = [
    {
      title: "Prohibitive Advertising Costs",
      description: "Small businesses, local entrepreneurs, and individual service providers face significant barriers to outdoor advertising. Traditional billboard placements, static signage, and premium advertising spaces demand substantial financial investment, effectively excluding emerging businesses from physical advertising channels.",
    },
    {
      title: "Underutilized Vehicle Assets",
      description: "Commercial vehicle operators, particularly auto-rickshaw drivers and three-wheeler owners, experience substantial idle time throughout their workday. This represents undermonetized vehicle surface area and uncompensated travel time that could generate supplementary income without disrupting primary business operations.",
    },
  ];

  const advertiserBenefits = [
    { title: "Cost Efficiency", desc: "Nominal advertising rates democratize access to outdoor marketing, enabling budget-conscious businesses to compete for audience attention" },
    { title: "Geographic Targeting", desc: "Mobile ad displays reach diverse neighborhoods and high-traffic areas throughout the day, maximizing exposure across urban landscapes" },
    { title: "Campaign Flexibility", desc: "Digital platform enables rapid deployment, real-time adjustments, and performance tracking unavailable with static advertising" },
    { title: "Measurable Reach", desc: "Analytics and impression tracking provide transparency into campaign performance and return on investment" },
  ];

  const driverBenefits = [
    { title: "Zero Operational Change", desc: "Drivers maintain normal business activities while earning supplementary income from ad displays during existing routes" },
    { title: "Idle Time Monetization", desc: "Previously uncompensated vehicle downtime and passenger search periods now generate revenue" },
    { title: "Commission Structure", desc: "Performance-based earnings tied to ad display time and distance covered incentivize participation" },
    { title: "Asset Appreciation", desc: "Vehicle transformation into an advertising medium increases overall earning potential without additional labor" },
  ];

  const screens = [
    {
      number: "01",
      title: "Sign In & Authentication",
      description: "Secure access gateway with role-based authentication separating advertiser and vehicle owner experiences. Social login integration reduces friction during initial onboarding.",
      images: [
        "https://phrygian-tornado-507.notion.site/image/attachment%3A9753bc56-1a8b-4d5b-9f96-6ff49ed6a141%3ASystems_Thinking_-_iPhone_12_Pro_Max_-_1.png?table=block&id=30a22ee1-b108-80bf-91ec-fc71856fdac6&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=480&userId=&cache=v2",
        "https://phrygian-tornado-507.notion.site/image/attachment%3Ad5b50599-f6d8-4f8d-a992-e00a19fa21cf%3AiPhone_13_Mockup-5.png?table=block&id=30a22ee1-b108-8095-9f5e-fefe9cc5de9a&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=480&userId=&cache=v2",
      ],
    },
    {
      number: "02",
      title: "Dashboard (Role-Specific)",
      description: "Advertiser Dashboard: Campaign overview, active campaign status, performance snapshots, wallet balance, and quick-access campaign creation. Driver Dashboard: Earnings summary, active ad displays, vehicle status, upcoming payouts, and platform notifications.",
      images: [
        "https://phrygian-tornado-507.notion.site/image/attachment%3A968e6f16-4941-4d1a-9a0d-1642364902a2%3ASystems_Thinking_-_iPhone_12_Pro_Max_-_2.png?table=block&id=30a22ee1-b108-807c-adf1-ec8f72592804&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=480&userId=&cache=v2",
        "https://phrygian-tornado-507.notion.site/image/attachment%3Af704b36d-6d7e-485f-b043-6bc98fd573b0%3AiPhone_13_Mockup-4.png?table=block&id=30a22ee1-b108-80a6-883a-fc89675edba7&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=480&userId=&cache=v2",
      ],
    },
    {
      number: "03",
      title: "Create Campaign",
      description: "Guided campaign builder with progressive disclosure of configuration options. Real-time pricing calculator adjusts estimates based on selected parameters. Creative upload interface with format validation and preview capabilities.",
      images: [
        "https://phrygian-tornado-507.notion.site/image/attachment%3A3d125d4f-0341-4160-a611-60ac9543c6b6%3ASystems_Thinking_-_iPhone_12_Pro_Max_-_3.png?table=block&id=30a22ee1-b108-808b-aa0a-c3b82573946a&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=480&userId=&cache=v2",
        "https://phrygian-tornado-507.notion.site/image/attachment%3Afc3d98b7-c0d3-4301-8a23-4b4ad0ecf8ab%3AiPhone_13_Mockup-3.png?table=block&id=30a22ee1-b108-80f5-ae29-e204b91d80ee&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=480&userId=&cache=v2",
      ],
    },
    {
      number: "04",
      title: "Payments & Wallet",
      description: "Unified payment management for campaign funding and payout processing. Multiple payment method support including cards, UPI, net banking, and digital wallets.",
      images: [
        "https://phrygian-tornado-507.notion.site/image/attachment%3Ae0b6c4c5-8453-445c-89e3-5bd08d24a19a%3ASystems_Thinking_-_iPhone_12_Pro_Max_-_4.png?table=block&id=30a22ee1-b108-80ef-a8af-cbb0f5f2f610&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=480&userId=&cache=v2",
        "https://phrygian-tornado-507.notion.site/image/attachment%3A9ea28fad-b2c2-4274-8b7f-292f2508b694%3AiPhone_13_Mockup-2.png?table=block&id=30a22ee1-b108-808f-9e02-d39bbecedc22&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=480&userId=&cache=v2",
      ],
    },
    {
      number: "05",
      title: "Campaign History",
      description: "Comprehensive archive of past campaigns with performance analytics, comparative insights, and creative library.",
      images: [
        "https://phrygian-tornado-507.notion.site/image/attachment%3Acdf8c1f4-9186-4e83-81f8-2c6bc3cfca8b%3ASystems_Thinking_-_iPhone_12_Pro_Max_-_6.png?table=block&id=30a22ee1-b108-808e-93d5-f78c9256734e&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=480&userId=&cache=v2",
        "https://phrygian-tornado-507.notion.site/image/attachment%3A1ad28ff0-f0d6-4711-9424-79c07287235b%3AiPhone_13_Mockup.png?table=block&id=30a22ee1-b108-8053-a865-cce42e8baf2c&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=480&userId=&cache=v2",
      ],
    },
  ];

  const impactMetrics = [
    { title: "Income Augmentation", desc: "Supplementary earnings improve financial stability for commercial vehicle operators" },
    { title: "Small Business Enablement", desc: "Reduced advertising barriers empower local entrepreneurs to compete effectively" },
    { title: "Urban Economic Activity", desc: "Increased small business visibility drives local commerce and neighborhood vitality" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Back Button */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="fixed top-6 left-6 z-50"
      >
        <Button
          onClick={() => navigate("/#projects")}
          variant="outline"
          className="no-shiny h-12 px-4 rounded-full border-white/10 bg-black/50 backdrop-blur-xl text-white hover:bg-white/10"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>
      </motion.div>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="mb-6 bg-orange-500/10 text-orange-400 border-orange-500/20">
              AdTech
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Syne'] text-white mb-6">
              Adeefly
            </h1>
            <p className="text-xl md:text-2xl text-white/60 max-w-3xl mb-10">
              Democratizing Outdoor Advertising Through Mobile LED Networks
            </p>

            {/* Meta Info */}
            <div className="flex flex-wrap gap-6 mb-12">
              <div className="flex items-center gap-2 text-white/50">
                <User className="w-5 h-5" />
                <span>Service Designer & UX Strategist</span>
              </div>
              <div className="flex items-center gap-2 text-white/50">
                <Calendar className="w-5 h-5" />
                <span>Service Design - AdTech Platform</span>
              </div>
              <div className="flex items-center gap-2 text-white/50">
                <Wrench className="w-5 h-5" />
                <span>Two-Sided Marketplace, Service Blueprint</span>
              </div>
            </div>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="rounded-3xl overflow-hidden border border-white/5"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_web-creator-900/artifacts/ie0akgpz_Gemini_Generated_Image_9y3bso9y3bso9y3b.png"
              alt="Adeefly Platform"
              className="w-full"
            />
          </motion.div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-20 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sm font-mono text-orange-400 tracking-wider uppercase mb-4">
              Project Overview
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white leading-tight max-w-4xl mb-8">
              Connecting Vehicle Owners with Advertisers
            </h2>
            <p className="text-lg text-white/60 max-w-4xl leading-relaxed">
              The platform enables three-wheeler operators and commercial drivers to monetize their vehicles' idle time and daily routes by hosting LED-based advertisements. Simultaneously, individuals and businesses gain access to affordable, high-visibility outdoor advertising that rivals traditional media reach at a fraction of&nbsp;the&nbsp;cost.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Problem Space */}
      <section className="py-20 px-6 md:px-12 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-orange-400 tracking-wider uppercase mb-4">
              Problem Space & Market Opportunity
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              Dual Market Challenges
            </h2>
          </motion.div>

          <div>
            {challenges.map((challenge, index) => (
              <motion.div
                key={challenge.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`p-8 rounded-2xl bg-white/5 border border-white/5${index > 0 ? ' mt-8' : ''}`}
              >
                <h3 className="text-xl font-bold font-['Syne'] text-white mb-4">
                  Challenge {index + 1}: {challenge.title}
                </h3>
                <p className="text-white/60 leading-relaxed">{challenge.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-orange-400 tracking-wider uppercase mb-4">
              Value Proposition
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              For Advertisers: Accessible Outdoor Marketing
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6 mb-16">
            {advertiserBenefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-white/5 border border-white/5"
              >
                <h3 className="text-lg font-bold text-white mb-3">{benefit.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              For Vehicle Owners: Passive Income Generation
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6">
            {driverBenefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-white/5 border border-white/5"
              >
                <h3 className="text-lg font-bold text-white mb-3">{benefit.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-12 rounded-3xl overflow-hidden bg-white p-6"
          >
            <img
              src="https://phrygian-tornado-507.notion.site/image/attachment%3A9987275a-9359-45b1-89a0-bc4588d1b6fc%3AGroup_104.png?table=block&id=30922ee1-b108-804c-9f7f-eb1c26848a34&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=1150&userId=&cache=v2"
              alt="Value Proposition"
              className="w-full max-h-[70vh] object-contain mx-auto"
            />
          </motion.div>
        </div>
      </section>

      {/* Application Architecture */}
      <section className="py-20 px-6 md:px-12 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-orange-400 tracking-wider uppercase mb-4">
              Application Architecture
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              Platform Structure
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-3xl overflow-hidden bg-white p-6"
          >
            <img
              src="https://phrygian-tornado-507.notion.site/image/attachment%3Aefc4babb-c403-4a12-8504-12eb51128973%3Aimage_43.png?table=block&id=30922ee1-b108-805e-a8e0-e196b8a672d8&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=1730&userId=&cache=v2"
              alt="Application Architecture"
              className="w-full max-h-[70vh] object-contain mx-auto"
            />
          </motion.div>
        </div>
      </section>

      {/* Key Screens */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <p className="text-sm font-mono text-orange-400 tracking-wider uppercase mb-4">
              Key Screens
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              Application Interface
            </h2>
          </motion.div>

          <div>
            {screens.map((screen, index) => (
              <motion.div
                key={screen.number}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={index > 0 ? 'mt-16' : ''}
              >
                <div className="mb-8">
                  <span className="text-5xl font-bold text-white/10 font-['Syne']">{screen.number}</span>
                  <h3 className="text-2xl font-bold font-['Syne'] text-white mt-2 mb-4">
                    {screen.title}
                  </h3>
                  <p className="text-white/60 max-w-2xl leading-relaxed">{screen.description}</p>
                </div>
                <div className="flex gap-6 justify-center flex-wrap">
                  {screen.images.map((img, imgIndex) => (
                    <div key={imgIndex} className="rounded-2xl overflow-hidden border border-white/5 w-64">
                      <img src={img} alt={`${screen.title} ${imgIndex + 1}`} className="w-full" />
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact */}
      <section className="py-20 px-6 md:px-12 bg-gradient-to-b from-[#0d0d0d] to-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-orange-400 tracking-wider uppercase mb-4">
              Potential Impact
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              Social & Economic Impact
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {impactMetrics.map((metric, index) => (
              <motion.div
                key={metric.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-white/5 border border-white/5"
              >
                <h3 className="text-lg font-bold text-white mb-3">{metric.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{metric.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-12 rounded-3xl overflow-hidden"
          >
            <img
              src="https://phrygian-tornado-507.notion.site/image/attachment%3A94ee70c7-60da-4553-a178-8809bdb93ad0%3AGemini_Generated_Image_lcfdp6lcfdp6lcfd.jpg?table=block&id=31622ee1-b108-8000-a484-d41d388b1786&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2"
              alt="Impact Visual"
              className="w-full"
            />
          </motion.div>
        </div>
      </section>

      {/* Conclusion */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sm font-mono text-orange-400 tracking-wider uppercase mb-4">
              Conclusion
            </p>
            <h2 className="text-2xl md:text-3xl font-bold font-['Syne'] text-white mb-6 leading-relaxed">
              Adeefly demonstrates service design innovation at the intersection of advertising technology, gig economy mechanics, and urban infrastructure utilization. By identifying underutilized assets and matching them with unmet market needs, the platform creates mutual value across distinct stakeholder groups while democratizing access to outdoor advertising.
            </h2>
          </motion.div>
        </div>
      </section>

      {/* Navigation Footer */}
      <section className="py-16 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Button
              onClick={() => navigate("/#projects")}
              variant="outline"
              className="no-shiny h-12 px-6 rounded-full border-white/10 text-white hover:bg-white/5 transition-all"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Portfolio
            </Button>
            
            <Button
              onClick={() => navigate("/#contact")}
              className="h-12 px-8 rounded-full bg-white text-black font-semibold hover:bg-white/90 transition-all"
            >
              Get in Touch
            </Button>
            
            <Button
              onClick={() => navigate("/projects/yelp-mentorship-platform")}
              variant="outline"
              className="no-shiny h-12 px-6 rounded-full border-white/10 text-white hover:bg-white/5 transition-all"
            >
              Next: Yelp Platform
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdeeflyProject;
